// Import core-js for polyfills, mostly for IE11
import "core-js/stable";
import "regenerator-runtime/runtime";
import "intersection-observer";
import "./polyfills/append.polyfill";
import Vue from "vue";
import Vuex from "vuex";
import router from "./router";
import PortalVue from "portal-vue";
import VueObserveVisibility from "vue-observe-visibility";
import { breakpoint } from "./mixins/breakpoint.js";
import reveal from "./directives/reveal.js";
/* eslint-disable no-unused-vars */
import { VBTooltip, TablePlugin, ImagePlugin, AlertPlugin } from "bootstrap-vue";
/* eslint-enable no-unused-vars */
import { hasSlot } from "./mixins/has-slot.js";
import { formatTelephoneField } from "./directives/format-telephone-field.js";
import { oneOfFieldsRequired } from "./directives/one-of-fields-required";
import svg4everybody from "svg4everybody/dist/svg4everybody.min.js";
import "./components/form/form-validation";
import "./services/parameter-tracker";
import PurewickDataService from "./services/purewick-data.service";
import "./components/form/form-hidden-value-updater";
import "./components/chooser/chooser-tracking-service";
svg4everybody();
import "./sass/main.scss";

// Instantiate any needed classes or services.
new PurewickDataService();

// register plugins
Vue.use(PortalVue);
Vue.use(VueObserveVisibility);
Vue.use(TablePlugin);
Vue.use(ImagePlugin);

// register global mixins
Vue.mixin(breakpoint);
Vue.mixin(hasSlot);

// Import Vuex store
import store from "./store";
Vue.use(Vuex);

/**
 * Directives.
 */
Vue.directive("b-tooltip", VBTooltip);
Vue.directive("lms-reveal", reveal);
Vue.directive("lms-format-telephone-field", formatTelephoneField);
Vue.directive("lms-one-of-fields-required", oneOfFieldsRequired);

/**
 * Components to be loaded globally (always in the bundle)
 */
Vue.component(
  "lms-masthead",
  require("./components/masthead/masthead.vue").default
);
Vue.component(
  "lms-navigation",
  require("./components/navigation/navigation.vue").default
);
Vue.component(
  "lms-skip-nav",
  require("./components/skip-nav/skip-nav.vue").default
);
Vue.component(
  "lms-query-params",
  require("./components/query-params/query-params.vue").default
);
Vue.component(
  "lms-hamburger",
  require("./components/hamburger/hamburger.vue").default
);
Vue.component("lms-icon", require("./components/icon/icon.vue").default);
Vue.component("lms-hero", require("./components/hero/hero.vue").default);
Vue.component("lms-hero-slider", require("./components/hero-slider/hero-slider.vue").default);
Vue.component(
  "lms-nav-search",
  require("./components/nav-search/nav-search.vue").default
);
Vue.component(
  "lms-nav-slide",
  require("./components/navigation/nav-slide.vue").default
);
Vue.component("lms-search", require("./components/search/search.vue").default);
Vue.component(
  "collapse-transition",
  require("vue2-transitions").CollapseTransition
);
Vue.component("lms-footer", require("./components/footer/footer.vue").default);
Vue.component(
  "slide-x-left-transition",
  require("vue2-transitions").SlideXLeftTransition
);
Vue.component(
  "lms-wrapper",
  require("./components/wrapper/wrapper.vue").default
);
Vue.component("lms-table", require("./components/table/table.vue").default);
Vue.component(
  "lms-switcher",
  require("./components/switcher/switcher.vue").default
);
Vue.component(
  "lms-file-upload",
  require("./components/file-upload/file-upload.vue").default
);
Vue.component(
  "lms-ipi",
  require("./components/insurance-provider-information/insurance-provider-information.vue")
    .default
);
Vue.component(
  "lms-participating-insurers",
  require("./components/participating-insurers/participating-insurers.vue").default
);

/**
 * Dynamic Imports
 * For components that should only be loaded on pages which use them,
 * import them dynamically here. The 'webpackChunkName' specifies how the
 * saved chunk file will be named (ie, showcase-lazy.js). Chunked files
 * are saved in the 'dist' folder in the theme root.
 */
const LmsAccordion = () =>
  import(
    /* webpackChunkName: "accordion-lazy" */ "./components/accordion/accordion.vue"
  );
const LmsAccordionGroup = () =>
  import(
    /* webpackChunkName: "accordion-group-lazy" */ "./components/accordion/accordion-group.vue"
  );
const LmsVideo = () =>
  import(/* webpackChunkName: "video-lazy" */ "./components/video/video.vue");
const LmsModal = () =>
  import(/* webpackChunkName: "modal-lazy" */ "./components/modal/modal.vue");
const LmsResults = () =>
  import(
    /* webpackChunkName: "results-lazy" */ "./components/results/results.vue"
  );
const LmsFilter = () =>
  import(
    /* webpackChunkName: "filter-lazy" */ "./components/filter/filter.vue"
  );
const LmsFilterSearch = () =>
  import(
    /* webpackChunkName: "filter-search-lazy" */ "./components/filter/filter-search.vue"
  );
const LmsTextCarousel = () =>
  import(
    /* webpackChunkName: "text-carousel-lazy" */ "./components/text-carousel/text-carousel.vue"
  );
const LmsShare = () =>
  import(/* webpackChunkName: "share-lazy" */ "./components/share/share.vue");

const LmsGallery = () =>
  import(
    /* webpackChunkName: "gallery-lazy" */ "./components/gallery/gallery.vue"
  );
const LmsChooser = () =>
  import(
    /* webpackChunkName: "chooser-lazy" */ "./components/chooser/chooser.vue"
  );
const LmsCallUsSticky = () =>
  import(
    /* webpackChunkName: "call-us-sticky-lazy" */ "./components/call-us-sticky/call-us-sticky.vue"
  );
const LmsAlert = () =>
  import(/* webpackChunkName: "alert-lazy" */ "./components/alert/alert.vue");
const LmsSearchResults = () =>
  import(
    /* webpackChunkName: "search-results-lazy" */ "./components/search/search-results.vue"
  );

/**
 * These dynamic imports are also lazy loaded, but are loaded via
 * rel="prefetch" to defer their loading until after other scripts
 */
const LmsRevelator = () =>
  import(
    /* webpackChunkName: "revelator-lazy" */ /* webpackPrefetch: true */ "./components/revelator/revelator.vue"
  );

Vue.config.productionTip = false;

/* eslint-disable no-new */
new Vue({
  el: "#app",
  store,
  router,
  comments: true,
  delimiters: ["${", "}"],
  components: {
    LmsAccordion,
    LmsAccordionGroup,
    LmsVideo,
    LmsModal,
    LmsFilter,
    LmsFilterSearch,
    LmsResults,
    LmsRevelator,
    LmsTextCarousel,
    LmsShare,
    LmsGallery,
    LmsChooser,
    LmsCallUsSticky,
    LmsAlert,
    LmsSearchResults,
  },
  created() {
    // If a search query string exists,
    // add it to the global store
    store.dispatch("setQuery");
  },
});

// IE11
let isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

if (isIE11) {
  confirm('This site has been optimized for the latest release of Chrome, Firefox, or Safari.')
}
